import { Component, Input, OnInit } from '@angular/core';
import {
  FormBuilder,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Modal } from 'bootstrap';
import { Food } from 'src/app/interfaces/food';
import { FoodService } from 'src/app/services/food.service';
import { StateService } from 'src/app/services/state.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-food-form-modal',
  templateUrl: './food-form-modal.component.html',
  styleUrls: ['./food-form-modal.component.scss'],
})
export class FoodFormModalComponent implements OnInit {
  foodForm: UntypedFormGroup;
  editing: boolean = false;
  selectedFood?: Food;

  constructor(
    private fb: FormBuilder,
    private toastService: ToastService,
    private stateService: StateService,
    private foodService: FoodService
  ) {
    this.foodForm = this.fb.group({
      name: [null, [Validators.required]],
      proteins: [null, [Validators.required]],
      carbohydrates: [null, [Validators.required]],
      saturatedFats: [null, [Validators.required]],
      unsaturatedFats: [null, [Validators.required]],
      kcal: [null, [Validators.required]],
      portionMeasure: ['100GR', [Validators.required]],
      fiber: [null],
      vitamin_a: [null],
      vitamin_c: [null],
      vitamin_d: [null],
      vitamin_e: [null],
      vitamin_k: [null],
      vitamin_b1: [null],
      vitamin_b2: [null],
      vitamin_b3: [null],
      vitamin_b5: [null],
      vitamin_b6_biotin: [null],
      vitamin_b6_pyridoxine: [null],
      vitamin_b9: [null],
      vitamin_b12: [null],
      potassium: [null],
      chlorine: [null],
      sodium: [null],
      calcium: [null],
      iron: [null],
      phosphorus: [null],
      copper: [null],
      iodine: [null],
      magnesium: [null],
      zinc: [null],
      manganese: [null],
      chromium: [null],
      molybdenum: [null],
      cobalt: [null],
      selenium: [null],
      type: ['FOOD', [Validators.required]], // Add this line
    });
  }

  ngOnInit(): void {
    this.stateService.edition$.subscribe({
      next: (editing) => {
        if (editing) {
          this.completeFieldsWithData();
        }
      },
    });
  }

  completeFieldsWithData() {
    this.editing = true;
    this.selectedFood = this.stateService.selectedFood;

    Object.keys(this.foodForm.controls).forEach(
      (controlName: keyof Food | string) => {
        this.foodForm.controls[controlName].setValue(
          this.stateService.selectedFood![controlName as keyof Food]
        );
      }
    );
  }

  editFood() {
    Object.keys(this.foodForm.controls).forEach(
      (controlName: keyof Food | string) => {
        this.selectedFood![controlName as keyof Food] =
          this.foodForm.controls[controlName].value;
      }
    );

    this.foodService.updateFood(this.selectedFood!).subscribe({
      next: () => {
        this.openFoodListModal();
      },
      error: () => {
        this.toastService.alert('Hubo un error en la base de datos.');
      },
    });
  }

  createFood() {
    let food: any | Food = {};

    Object.keys(this.foodForm.controls).forEach((controlName: string) => {
      if (this.foodForm.controls[controlName]) {
        food[controlName] = this.foodForm.controls[controlName].value;
      } else {
        food[controlName] = 0;
      }
    });

    this.foodService.createFood(food).subscribe({
      next: () => {
        this.openFoodListModal();
      },
      error: () => {
        this.toastService.alert('Hubo un error en la base de datos.');
      },
    });
  }

  onSubmit() {
    if (this.editing) {
      this.editFood();
    } else {
      this.createFood();
    }
  }

  openFoodListModal() {
    this.clearAllFields();

    this.stateService.openModal.hide();

    const foodListModal = new Modal('#foodListModal');
    this.stateService.openModal = foodListModal;

    foodListModal.show();
  }

  clearAllFields() {
    Object.keys(this.foodForm.controls).forEach((controlName) => {
      this.foodForm.get(controlName)?.setValue(null);
    });
    this.name.setValue('');

    this.editing = false;
    this.selectedFood = undefined;
    this.stateService.selectedFood = undefined;
  }

  get name() {
    return this.foodForm.controls['name'];
  }

  get proteins() {
    return this.foodForm.controls['proteins'];
  }

  get carbohydrates() {
    return this.foodForm.controls['carbohydrates'];
  }

  get saturatedFats() {
    return this.foodForm.controls['saturatedFats'];
  }

  get unsaturatedFats() {
    return this.foodForm.controls['unsaturatedFats'];
  }

  get kcal() {
    return this.foodForm.controls['kcal'];
  }

  get portionMeasure() {
    return this.foodForm.controls['portionMeasure'];
  }

  get vitaminA() {
    return this.foodForm.controls['vitamin_a'];
  }

  get vitaminC() {
    return this.foodForm.controls['vitamin_c'];
  }

  get vitaminD() {
    return this.foodForm.controls['vitamin_d'];
  }

  get vitaminE() {
    return this.foodForm.controls['vitamin_e'];
  }

  get vitaminK() {
    return this.foodForm.controls['vitamin_k'];
  }

  get vitaminB1() {
    return this.foodForm.controls['vitamin_b1'];
  }

  get vitaminB2() {
    return this.foodForm.controls['vitamin_b2'];
  }

  get vitaminB3() {
    return this.foodForm.controls['vitamin_b3'];
  }

  get vitaminB5() {
    return this.foodForm.controls['vitamin_b5'];
  }

  get vitaminB6Biotin() {
    return this.foodForm.controls['vitamin_b6_biotin'];
  }

  get vitaminB6Pyridoxine() {
    return this.foodForm.controls['vitamin_b6_pyridoxine'];
  }

  get vitaminB9() {
    return this.foodForm.controls['vitamin_b9'];
  }

  get vitaminB12() {
    return this.foodForm.controls['vitamin_b12'];
  }

  get potassium() {
    return this.foodForm.controls['potassium'];
  }

  get chlorine() {
    return this.foodForm.controls['chlorine'];
  }

  get sodium() {
    return this.foodForm.controls['sodium'];
  }

  get calcium() {
    return this.foodForm.controls['calcium'];
  }

  get iron() {
    return this.foodForm.controls['iron'];
  }

  get phosphorus() {
    return this.foodForm.controls['phosphorus'];
  }

  get copper() {
    return this.foodForm.controls['copper'];
  }

  get iodine() {
    return this.foodForm.controls['iodine'];
  }

  get magnesium() {
    return this.foodForm.controls['magnesium'];
  }

  get zinc() {
    return this.foodForm.controls['zinc'];
  }

  get manganese() {
    return this.foodForm.controls['manganese'];
  }

  get chromium() {
    return this.foodForm.controls['chromium'];
  }

  get molybdenum() {
    return this.foodForm.controls['molybdenum'];
  }

  get cobalt() {
    return this.foodForm.controls['cobalt'];
  }

  get selenium() {
    return this.foodForm.controls['selenium'];
  }

  get type() {
    return this.foodForm.controls['type'];
  }
}
